/**
 * `/templates` — every template in this workspace, email and WhatsApp together.
 *
 * The row says four things, and each one is a decision someone is about to make
 * with it: what it is called, whether a campaign may pick it, how much is in it,
 * and whether the compiled email is up to date with the blocks. Nothing here is
 * derived on the client — `blockCount`, `status` and `compiledAt` are columns
 * the API returned on this request, and a template that has never compiled says
 * **never** rather than showing a plausible date. That is the zero-versus-absent
 * rule on this screen: "compiled at the epoch" and "has never been compiled" are
 * different facts and only one of them is ever true.
 *
 * Creating happens here rather than on a screen of its own. A new email template
 * needs exactly one thing — a name — and the API seeds the rest: a heading, a
 * paragraph and the unsubscribe footer. So the create step is a single field
 * that lands you in the editor, instead of a wizard that asks nothing.
 *
 * ## Slice 17 — what came out of the empty state, and what did not
 *
 * `FirstTemplate` used to run to two full paragraphs explaining what a template
 * *is*, that it is built from blocks rather than typed as a page, and that the
 * preview is the compile rather than an impression of it. Most of that was
 * written for the next builder rather than for the person looking at it, and it
 * lives in this header now: **a template is a stack of blocks that the API
 * compiles; the same compile is what the preview renders and what a campaign
 * sends.** The screen keeps the two sentences a reader would draw a false
 * conclusion without — that the preview is the compile, and that the unsubscribe
 * footer cannot be removed — because the second is a promise about somebody's
 * own AWS account and the first is the reason to trust the screen at all.
 *
 * **The footnote on the preview promise survives intact and is not simplified
 * back.** With click tracking on, SES rewrites every link *after* Wend has
 * handed the message over, so "what you see is what arrives" is false for some
 * workspaces. This screen has not read the workspace's tracking settings and has
 * no business reading them to fill in an empty state, so it makes the narrower
 * claim that is true for every workspace — the preview is the compile — and
 * leaves the editor, which has read them, to say what Amazon then does.
 * Claiming less here is cheaper than claiming wrong.
 */
import {
  DEFAULT_PAGE_SIZE,
  WA_TEMPLATE_CATEGORY_VALUES,
  createTemplateRequestSchema,
  templateSortSchema,
  type TemplateSummary,
  type WaTemplateCategory,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  Select,
  Spinner,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import { FileText, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { TemplateStatusBadge } from '../../components/TemplateStatusBadge';
import { Textarea } from '../../components/Textarea';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { createTemplate, listTemplates } from '../../lib/api/templates';
import { formatDateTime, pluralise } from '../../lib/format';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { useAsyncData } from '../../lib/use-async-data';
import { WA_CATEGORY_DISPLAY } from '../../lib/whatsapp-display';
import { MoreDetail } from './MoreDetail';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

export function TemplatesPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'updatedAt', direction: 'desc' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const sortField = templateSortSchema.catch('updatedAt').parse(sort.key);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listTemplates(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [page, search, sortField, sort.direction],
  );

  const columns = useMemo<Array<DataTableColumn<TemplateSummary>>>(
    () => [
      {
        key: 'name',
        header: 'Template',
        sortable: true,
        cell: (template) => (
          <div className="min-w-0">
            <Link
              to={`/templates/${template.id}`}
              className="font-medium text-text underline-offset-4 hover:underline"
            >
              {template.name}
            </Link>
            {/* The subject, because it is what a recipient sees first and it is
                the one field approval refuses a template for not having. */}
            {template.subject === null ? (
              <p className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
                no subject yet
              </p>
            ) : (
              <p className="truncate text-[0.82rem] text-muted" title={template.subject}>
                {template.subject}
              </p>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: '9rem',
        cell: (template) => <TemplateStatusBadge status={template.status} />,
      },
      {
        key: 'blocks',
        header: 'Blocks',
        align: 'right',
        width: '7rem',
        cell: (template) => (
          <span className="font-mono text-[0.85rem] tabular-nums text-text">
            {template.blockCount}
          </span>
        ),
      },
      {
        key: 'compiled',
        header: 'Compiled',
        width: '12rem',
        cell: (template) =>
          template.compiledAt === null ? (
            <span className="font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
              never
            </span>
          ) : (
            <span className="whitespace-nowrap text-muted">
              {formatDateTime(template.compiledAt)}
            </span>
          ),
      },
      {
        key: 'updatedAt',
        header: 'Edited',
        sortable: true,
        width: '12rem',
        cell: (template) => (
          <span className="whitespace-nowrap text-muted">{formatDateTime(template.updatedAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <AppLayout
      title="Templates"
      actions={
        <Button
          type="button"
          size="sm"
          leadingIcon={<Plus aria-hidden="true" />}
          onClick={() => {
            setCreating((open) => !open);
          }}
        >
          New template
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        {creating && (
          <CreateTemplateCard
            onCancel={() => {
              setCreating(false);
            }}
          />
        )}

        {failedToLoad ? (
          <Card>
            <ErrorState error={error} title="Could not load your templates" onRetry={reload} />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search by template name"
                  aria-label="Search templates"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the templates" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'template')}
                </p>
              )}
            </div>

            {/*
              A refresh that failed while a list is already on screen. `ErrorState`
              is the wrong instrument here — it replaces the thing it reports on,
              and there is a perfectly good list underneath — so this says what
              failed, quotes the request id, and leaves the stale rows labelled as
              stale. The screen that could not load at all is the branch above.
            */}
            {error !== null && (
              <FormAlert
                tone="error"
                {...(isApiError(error) && error.requestId !== undefined
                  ? { requestId: error.requestId }
                  : {})}
              >
                {errorMessage(error)} The rows below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Email templates"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(template) => template.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="54rem"
              empty={
                search === '' ? (
                  <FirstTemplate
                    onCreate={() => {
                      setCreating(true);
                    }}
                  />
                ) : (
                  <NoMatch search={search} />
                )
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </div>
    </AppLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Creating                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The create form: the channel, a name, then straight into the editor.
 *
 * **The channel is chosen once, here, and never again.** A template is email or
 * WhatsApp for its whole life — the database refuses a row that is half of each,
 * because the two have nothing in common: one has blocks and an unsubscribe
 * footer, the other has a category Meta reviews and positional variables. A
 * "convert to WhatsApp" action would be a new template with the old one's name.
 *
 * The email arm deliberately sends **no blocks**, which is what makes the API
 * seed `createDefaultDocument` — a heading, a paragraph and the unsubscribe
 * footer — so the footer guarantee lives in one place. The WhatsApp arm has
 * nothing equivalent to seed: `waTemplateDocumentSchema` needs a category and a
 * body, and a body invented here would be Wend writing somebody's message. So it
 * asks for the first line and the composer takes it from there.
 */
function CreateTemplateCard({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<'email' | 'whatsapp'>('email');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<WaTemplateCategory>('marketing');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [failure, setFailure] = useState<{ message: string; requestId: string | undefined } | null>(
    null,
  );
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setFailure(null);

    const result = validate(
      createTemplateRequestSchema,
      channel === 'whatsapp'
        ? { name, type: 'whatsapp', whatsapp: { category, body } }
        : { name },
    );
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const template = await createTemplate(result.data);
      navigate(`/templates/${template.id}`);
    } catch (error) {
      const fieldErrors = apiFieldErrors(error);
      delete fieldErrors[ROOT_FIELD];
      setErrors(fieldErrors);
      setFailure({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1rem]">
          {channel === 'whatsapp' ? 'New WhatsApp template' : 'New email template'}
        </CardTitle>
        <CardDescription>
          {channel === 'whatsapp'
            ? 'The name is for you — recipients never see it. Meta reviews the message before it can be used.'
            : 'The name is for you — recipients never see it.'}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          noValidate
          className="flex max-w-xl flex-col gap-4"
        >
          {failure !== null && (
            <FormAlert
              tone="error"
              {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
            >
              {failure.message}
            </FormAlert>
          )}
          {errors[ROOT_FIELD] !== undefined && (
            <FormAlert tone="error">{errors[ROOT_FIELD]}</FormAlert>
          )}

          {/*
            "Fixed for the life of the template" is the load-bearing half and it
            stays on the field. Why the two kinds cannot be converted — different
            documents, a database that refuses a row that is half of each — is in
            this file's header and behind the disclosure, not in the label.
          */}
          <FormField
            label="Channel"
            required
            description="Fixed for the life of the template — it cannot be changed later."
          >
            <Select
              value={channel}
              disabled={pending}
              options={[
                { value: 'email', label: 'Email — sent through your Amazon SES account' },
                { value: 'whatsapp', label: 'WhatsApp — approved by Meta before it can be used' },
              ]}
              onValueChange={(value) => {
                setChannel(value === 'whatsapp' ? 'whatsapp' : 'email');
                setErrors({});
              }}
            />
          </FormField>

          <MoreDetail summary="Why a template cannot change channel later">
            <p>
              The two are different documents. An email is a stack of blocks ending in an
              unsubscribe footer; a WhatsApp template is a body with numbered variables and a
              category Meta reviews. The database refuses a row that is half of each, so
              “convert to WhatsApp” would be a new template wearing the old one’s name.
            </p>
          </MoreDetail>

          <FormField label="Template name" required error={errors.name}>
            <Input
              value={name}
              autoFocus
              placeholder={channel === 'whatsapp' ? 'Diwali offer — WhatsApp' : 'Diwali offer — email'}
              disabled={pending}
              onChange={(event) => {
                setName(event.target.value);
                setErrors((current) => withoutError(current, 'name'));
              }}
            />
          </FormField>

          {channel === 'whatsapp' && (
            <>
              <FormField
                label="Category"
                required
                description={WA_CATEGORY_DISPLAY[category].consequence}
                error={errors['whatsapp.category']}
              >
                <Select
                  value={category}
                  disabled={pending}
                  options={WA_TEMPLATE_CATEGORY_VALUES.map((value) => ({
                    value,
                    label: `${WA_CATEGORY_DISPLAY[value].label} — ${WA_CATEGORY_DISPLAY[value].purpose}`,
                  }))}
                  onValueChange={(value) => {
                    setCategory(value as WaTemplateCategory);
                  }}
                />
              </FormField>

              <FormField
                label="Message"
                required
                description="The first draft. Header, footer, buttons and numbered variables come next, in the composer."
                error={errors['whatsapp.body']}
              >
                <Textarea
                  rows={4}
                  value={body}
                  disabled={pending}
                  onChange={(event) => {
                    setBody(event.target.value);
                    setErrors((current) => withoutError(current, 'whatsapp.body'));
                  }}
                />
              </FormField>
            </>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" size="sm" loading={pending}>
              {pending ? 'Creating' : 'Create and open'}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The first-run state: what this is, why it is empty, and the one action that
 * fills it.
 *
 * The two paragraphs it used to carry are in this file's header. What is left is
 * the pair of sentences a reader would draw a false conclusion without — the
 * preview is compiled by the code that sends, and the unsubscribe footer cannot
 * be removed — and one line of instruction folded away for anyone who wants to
 * know what a template is made of before they make one.
 */
function FirstTemplate({ onCreate }: { onCreate: () => void }) {
  return (
    <CardContent className="flex flex-col items-center gap-4 px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
        <FileText aria-hidden="true" className="size-5" />
      </span>

      <div className="flex max-w-[38rem] flex-col gap-2.5">
        <h2 className="text-[1.1rem] font-semibold tracking-[-0.022em]">No templates yet</h2>
        <p className="text-[0.92rem] leading-relaxed text-muted">
          A template is the message you send. Nothing has been made in this workspace yet.
        </p>
        <p className="text-[0.92rem] leading-relaxed text-muted">
          The preview is compiled by the same code that sends, and every email ends with an
          unsubscribe footer that cannot be removed.
        </p>
      </div>

      <Button type="button" size="sm" leadingIcon={<Plus aria-hidden="true" />} onClick={onCreate}>
        Create your first template
      </Button>

      <MoreDetail summary="What an email template is made of" className="text-left">
        <p>
          A stack of blocks — headings, paragraphs, images, buttons — plus a subject line. You
          write plain text; Wend compiles it into an email that holds together in the mail clients
          people actually use. A WhatsApp template is a different document: a body with numbered
          variables that Meta reviews before it can be used.
        </p>
      </MoreDetail>
    </CardContent>
  );
}

function NoMatch({ search }: { search: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <p className="text-[0.92rem] text-muted">
        No template matches <span className="text-text">“{search}”</span>.
      </p>
      <p className="text-[0.85rem] text-muted">Searching matches the template name.</p>
    </div>
  );
}
